import React from 'react';
import ReactMarkdown from 'react-markdown';
import { ChatMessage } from '../types';

interface TreatmentPlanDemoChatProps {
  messages: ChatMessage[];
  isLoading: boolean;
}

const TreatmentPlanDemoChat: React.FC<TreatmentPlanDemoChatProps> = ({ messages, isLoading }) => {
  const messagesEndRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="max-w-4xl mx-auto p-4">
        <div className="space-y-4">
          {messages.map((message) => (
              <div
                key={message.id}
                className={`flex w-full ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`
                    max-w-[85%] p-4 rounded-2xl
                    ${message.role === 'user'
                      ? 'bg-primary-600 text-white'
                      : 'bg-white border border-medical-200 shadow-sm'
                    }
                  `}
                >
                {/* Special handling for demo image */}
                {message.images && message.images.length > 0 && (
                  <div className="mb-3">
                    <img 
                      src="/crook.png"
                      alt="Patient Scan"
                      className="max-w-[300px] mx-auto rounded-lg"
                    />
                  </div>
                )}
                
                {message.role === 'assistant' ? (
                  <ReactMarkdown
                    className="prose prose-sm max-w-none"
                    components={{
                      p: ({ children }: any) => <p className="mb-3 last:mb-0">{children}</p>,
                      ul: ({ children }: any) => <ul className="list-disc list-inside mb-3 mt-3 space-y-1">{children}</ul>,
                      ol: ({ children }: any) => <ol className="list-decimal list-inside mb-3 mt-3 space-y-1">{children}</ol>,
                      li: ({ children }: any) => <li className="mb-1">{children}</li>,
                      strong: ({ children }: any) => <strong className="font-semibold">{children}</strong>,
                      em: ({ children }: any) => <em className="italic">{children}</em>,
                    }}
                  >
                    {message.content}
                  </ReactMarkdown>
                ) : (
                  <div className="whitespace-pre-wrap">{message.content}</div>
                )}

                <div className={`
                  text-xs mt-2 opacity-75
                  ${message.role === 'user' ? 'text-primary-100' : 'text-medical-500'}
                `}>
                  {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            </div>
          ))}

          {/* Typing Indicator */}
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-white border border-medical-200 rounded-2xl p-4 shadow-sm">
                <div className="flex items-center space-x-2">
                  <span className="text-medical-600">Typing</span>
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-medical-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-medical-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-medical-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>
    </div>
  );
};

export default TreatmentPlanDemoChat;